import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import { AdminButton, AdminCard, AdminField, EmptyState, adminInput } from "@/components/admin/ui";
import MenuQrCard from "@/pages/admin/MenuQrCard";

const EMPTY_ITEM = {
  name: "",
  description: "",
  price: "",
  price_type: "fixed",
  price_note: "",
  allergens: "",
  order: 0,
  visible: true,
  available_today: true,
  reservation_only: false,
  featured: false,
};

function ItemForm({ categoryId, item, onSaved, onCancel }) {
  const [form, setForm] = useState(
    item
      ? { ...item, price: item.price ?? "", allergens: (item.allergens || []).join(", ") }
      : { ...EMPTY_ITEM },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setBusy(true);
    setError("");
    const payload = {
      category_id: categoryId,
      name: form.name,
      description: form.description || "",
      price: form.price === "" ? null : Number(form.price),
      price_type: form.price_type || "fixed",
      price_note: form.price_note || "",
      allergens: form.allergens ? form.allergens.split(",").map((a) => a.trim()).filter(Boolean) : [],
      image: form.image || null,
      origin_note: form.origin_note || "",
      visible: !!form.visible,
      available_today: form.available_today !== false,
      reservation_only: !!form.reservation_only,
      featured: !!form.featured,
      order: Number(form.order) || 0,
    };
    try {
      if (item?.id) await api.put(`/admin/menu/items/${item.id}`, payload);
      else await api.post("/admin/menu/items", payload);
      onSaved();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-sage/40 bg-sage/5 p-4" data-testid="menu-item-form">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <AdminField label="Nome" className="lg:col-span-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={adminInput} data-testid="item-name" />
        </AdminField>
        <AdminField label="Prezzo (€)">
          <input type="number" step="0.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={adminInput} data-testid="item-price" />
        </AdminField>
        <AdminField label="Ordine">
          <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={adminInput} data-testid="item-order" />
        </AdminField>
        <AdminField label="Descrizione" className="sm:col-span-2 lg:col-span-3">
          <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={adminInput} data-testid="item-description" />
        </AdminField>
        <AdminField label="Allergeni (separati da virgola)">
          <input value={form.allergens} onChange={(e) => setForm({ ...form, allergens: e.target.value })} className={adminInput} />
        </AdminField>
        <AdminField label="Tipo di prezzo">
          <select
            value={form.price_type || "fixed"}
            onChange={(e) => setForm({ ...form, price_type: e.target.value })}
            className={adminInput}
            data-testid="item-price-type"
          >
            <option value="fixed">Fisso</option>
            <option value="per_hg">Al chilo (per hg)</option>
            <option value="on_request">Su richiesta / variabile</option>
          </select>
        </AdminField>
        <AdminField label="Nota prezzo (es. per 2 persone)">
          <input
            value={form.price_note || ""}
            onChange={(e) => setForm({ ...form, price_note: e.target.value })}
            className={adminInput}
            data-testid="item-price-note"
          />
        </AdminField>
      </div>
      <div className="mt-3 flex flex-wrap gap-5">
        <label className="flex items-center gap-2 text-sm text-foreground/70">
          <input type="checkbox" checked={!!form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--sage))]" data-testid="item-visible" />
          Visibile sul sito
        </label>
        <label className="flex items-center gap-2 text-sm text-foreground/70">
          <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--sage))]" data-testid="item-featured" />
          In evidenza in homepage
        </label>
        <label className="flex items-center gap-2 text-sm text-foreground/70">
          <input
            type="checkbox"
            checked={form.available_today !== false}
            onChange={(e) => setForm({ ...form, available_today: e.target.checked })}
            className="h-4 w-4 accent-[hsl(var(--sage))]"
            data-testid="item-available-today"
          />
          Disponibile oggi
        </label>
        <label className="flex items-center gap-2 text-sm text-foreground/70">
          <input
            type="checkbox"
            checked={!!form.reservation_only}
            onChange={(e) => setForm({ ...form, reservation_only: e.target.checked })}
            className="h-4 w-4 accent-[hsl(var(--sage))]"
            data-testid="item-reservation-only"
          />
          Solo su prenotazione
        </label>
      </div>
      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      <div className="mt-4 flex gap-2">
        <AdminButton onClick={save} disabled={busy} data-testid="item-save">
          Salva
        </AdminButton>
        <AdminButton variant="ghost" onClick={onCancel}>
          Annulla
        </AdminButton>
      </div>
    </div>
  );
}

export default function AdminMenu() {
  const [menu, setMenu] = useState([]);
  const [error, setError] = useState("");
  const [openCat, setOpenCat] = useState(null);
  const [editing, setEditing] = useState(null);
  const [newCat, setNewCat] = useState({ name: "", slug: "", description: "", order: 0 });
  const [showNewCat, setShowNewCat] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/menu");
      setMenu(data);
      setOpenCat((current) => {
        if (current && data.some((c) => c.id === current)) return current;
        return data.length ? data[0].id : null;
      });
    } catch (err) {
      setError(apiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveCategory = async () => {
    setError("");
    try {
      await api.post("/admin/menu/categories", {
        name: newCat.name,
        slug: newCat.slug || newCat.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        description: newCat.description,
        order: Number(newCat.order) || 0,
        visible: true,
      });
      setNewCat({ name: "", slug: "", description: "", order: 0 });
      setShowNewCat(false);
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const toggleCategory = async (cat) => {
    try {
      await api.put(`/admin/menu/categories/${cat.id}`, {
        name: cat.name,
        slug: cat.slug,
        description: cat.description || "",
        order: cat.order,
        visible: !cat.visible,
      });
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const toggleItem = async (item, patch) => {
    try {
      await api.put(`/admin/menu/items/${item.id}`, {
        category_id: item.category_id,
        name: item.name,
        description: item.description || "",
        price: item.price,
        price_type: item.price_type || "fixed",
        price_note: item.price_note || "",
        allergens: item.allergens || [],
        image: item.image || null,
        origin_note: item.origin_note || "",
        visible: item.visible,
        available_today: item.available_today !== false,
        reservation_only: !!item.reservation_only,
        featured: item.featured,
        order: item.order,
        ...patch,
      });
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/admin/menu/items/${id}`);
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const removeCategory = async (id) => {
    try {
      await api.delete(`/admin/menu/categories/${id}`);
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-menu">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.62rem] uppercase tracking-[0.2em] text-sage">Contenuti</p>
          <h1 className="mt-2 font-serif text-3xl md:text-4xl text-wood">Menu</h1>
        </div>
        <AdminButton variant="wood" onClick={() => setShowNewCat(!showNewCat)} data-testid="new-category">
          <Plus size={14} /> Categoria
        </AdminButton>
      </div>

      {error && <p className="border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive" data-testid="menu-admin-error">{error}</p>}

      <MenuQrCard />

      {showNewCat && (
        <AdminCard title="Nuova categoria">
          <div className="grid gap-3 sm:grid-cols-4">
            <AdminField label="Nome">
              <input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className={adminInput} data-testid="category-name" />
            </AdminField>
            <AdminField label="Slug (facoltativo)">
              <input value={newCat.slug} onChange={(e) => setNewCat({ ...newCat, slug: e.target.value })} className={adminInput} />
            </AdminField>
            <AdminField label="Descrizione" className="sm:col-span-2">
              <input value={newCat.description} onChange={(e) => setNewCat({ ...newCat, description: e.target.value })} className={adminInput} />
            </AdminField>
          </div>
          <div className="mt-4">
            <AdminButton onClick={saveCategory} data-testid="category-save">
              Salva categoria
            </AdminButton>
          </div>
        </AdminCard>
      )}

      {menu.length === 0 && <EmptyState title="Nessuna categoria" description="Crea la prima categoria del menu." />}

      <div className="space-y-3">
        {menu.map((cat) => (
          <section key={cat.id} className="border border-border bg-cream" data-testid={`category-${cat.slug}`}>
            <header className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
              <button
                type="button"
                onClick={() => setOpenCat(openCat === cat.id ? null : cat.id)}
                className="flex items-center gap-2 font-serif text-xl text-wood"
                data-testid={`toggle-category-${cat.slug}`}
              >
                <ChevronDown size={16} className={`transition-transform duration-300 ${openCat === cat.id ? "rotate-180" : ""}`} />
                {cat.name}
              </button>
              <span className="text-xs text-foreground/45">{cat.items.length} piatti</span>
              {!cat.visible && <span className="text-[0.6rem] uppercase tracking-[0.16em] text-destructive">nascosta</span>}
              <div className="ml-auto flex gap-2">
                <AdminButton variant="ghost" onClick={() => toggleCategory(cat)} data-testid={`hide-category-${cat.slug}`}>
                  {cat.visible ? "Nascondi" : "Mostra"}
                </AdminButton>
                <AdminButton variant="outline" onClick={() => setEditing({ categoryId: cat.id, item: null })} data-testid={`add-item-${cat.slug}`}>
                  <Plus size={13} /> Piatto
                </AdminButton>
                <AdminButton variant="danger" onClick={() => removeCategory(cat.id)}>
                  <Trash2 size={13} />
                </AdminButton>
              </div>
            </header>

            {openCat === cat.id && (
              <div className="p-4 space-y-3">
                {cat.items.length === 0 && <p className="text-sm text-foreground/55">Nessun piatto in questa categoria.</p>}
                {cat.items.map((item) =>
                  editing?.item?.id === item.id ? (
                    <ItemForm
                      key={item.id}
                      categoryId={cat.id}
                      item={item}
                      onCancel={() => setEditing(null)}
                      onSaved={() => {
                        setEditing(null);
                        load();
                      }}
                    />
                  ) : (
                    <div key={item.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-2 border border-border px-4 py-3" data-testid={`item-${item.id}`}>
                      <span className="font-serif text-lg text-wood">{item.name}</span>
                      {item.price != null && (
                        <span className="text-sm text-wood/60 tabular-nums">
                          € {formatPrice(item.price)}
                          {item.price_type === "per_hg" ? " / hg" : ""}
                        </span>
                      )}
                      {item.price_type === "on_request" && (
                        <span className="text-[0.6rem] uppercase tracking-[0.16em] text-sage">su richiesta</span>
                      )}
                      {item.available_today === false && (
                        <span className="text-[0.6rem] uppercase tracking-[0.16em] text-destructive">
                          non disponibile oggi
                        </span>
                      )}
                      {item.featured && <span className="text-[0.6rem] uppercase tracking-[0.16em] text-gold">in evidenza</span>}
                      {!item.visible && <span className="text-[0.6rem] uppercase tracking-[0.16em] text-destructive">nascosto</span>}
                      <span className="w-full text-sm text-foreground/60 sm:w-auto sm:flex-1 sm:truncate">{item.description}</span>
                      <div className="ml-auto flex gap-2">
                        <AdminButton variant="ghost" onClick={() => toggleItem(item, { visible: !item.visible })} data-testid={`hide-item-${item.id}`}>
                          {item.visible ? "Nascondi" : "Mostra"}
                        </AdminButton>
                        <AdminButton
                          variant="ghost"
                          onClick={() => toggleItem(item, { available_today: item.available_today === false })}
                          data-testid={`today-item-${item.id}`}
                        >
                          {item.available_today === false ? "Rimetti oggi" : "Finito oggi"}
                        </AdminButton>
                        <AdminButton variant="ghost" onClick={() => setEditing({ categoryId: cat.id, item })} data-testid={`edit-item-${item.id}`}>
                          Modifica
                        </AdminButton>
                        <AdminButton variant="danger" onClick={() => removeItem(item.id)} data-testid={`delete-item-${item.id}`}>
                          <Trash2 size={13} />
                        </AdminButton>
                      </div>
                    </div>
                  ),
                )}
                {editing?.categoryId === cat.id && !editing.item && (
                  <ItemForm
                    categoryId={cat.id}
                    onCancel={() => setEditing(null)}
                    onSaved={() => {
                      setEditing(null);
                      load();
                    }}
                  />
                )}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
